import { createEffect, JSX, onCleanup } from 'solid-js';
import DOMPurify from 'dompurify';

type ReleaseTableProps = {
  htmlContent: string;
  height?: string;
  maxHeight?: string;
  onClick?: () => void;
};

export const HTMLPreview = (props: ReleaseTableProps): JSX.Element => {
  let iframeRef: HTMLIFrameElement | undefined;

  createEffect(() => {
    const doc = iframeRef?.contentDocument;
    if (!doc) return;
    const sanitized = DOMPurify.sanitize(props.htmlContent, {
      WHOLE_DOCUMENT: true,
    });

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            html, body {
              margin: 0px !important;
              padding: 0px !important;
              overflow: ${
                props.height != null || props.maxHeight != null
                  ? 'auto'
                  : 'hidden'
              };
              min-height: 1px;
              cursor: ${props.onClick ? 'pointer' : 'default'};
              font-family: 'Roboto', sans-serif;
            }
            body::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }
            body::-webkit-scrollbar-track {
              background: #f1f1f1;
            }
            body::-webkit-scrollbar-thumb {
              background: #888;
              border-radius: 4px;
            }
            body::-webkit-scrollbar-thumb:hover {
              background: #555;
            }
          </style>
        </head>
        <body>
          ${sanitized}
        </body>
      </html>
    `);
    doc.close();

    if (props.onClick) {
      const handleClick = () => {
        props.onClick?.();
      };

      doc.addEventListener('click', handleClick);

      onCleanup(() => {
        doc.removeEventListener('click', handleClick);
      });
    }

    if (props.height == null) {
      const adjustIframeHeight = () => {
        if (iframeRef) {
          const contentHeight = doc.body.scrollHeight + 20;
          const currentMaxHeight =
            props.maxHeight != null ? parseFloat(props.maxHeight) : Infinity;
          let newHeight = Math.min(contentHeight, currentMaxHeight);
          newHeight = Math.max(newHeight, 60);

          iframeRef.style.height = `${newHeight}px`;
        }
      };
      requestAnimationFrame(adjustIframeHeight);
    }
  });

  return (
    <iframe
      ref={(el) => (iframeRef = el)}
      style={{
        width: '100%',
        padding: '16px',
        border: '1px solid #ccc',
        height: props.height ?? 'auto',
        'max-height': props.maxHeight ?? 'none',
        'min-height': '60px',
        overflow: 'hidden',
      }}
    />
  );
};
